import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  initializeAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type User,
} from "firebase/auth";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { trackSignUp } from "./analytics";
import { firebaseApp } from "./firebase";
import { demoMode } from "./mode";

export const firebaseAuth =
  firebaseApp && !demoMode
    ? initializeAuth(firebaseApp!, {
        persistence: browserLocalPersistence,
        popupRedirectResolver: browserPopupRedirectResolver,
      })
    : null;

export const realtimeTrafficEnabled = Boolean(firebaseApp && firebaseAuth);

const emailForSignInStorageKey = "juicytraffic.emailForSignIn";
const registrationChecks = new Map<string, Promise<void>>();

function registrationMethod(provider: string) {
  if (provider === "google.com") return "google";
  if (provider === "password") return "email";
  if (provider === "emailLink") return "email_link";
  return provider || "unknown";
}

function confirmRegistration(user: User) {
  const existing = registrationChecks.get(user.uid);
  if (existing) return existing;
  const check = (async () => {
    const token = await user.getIdToken();
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
    const response = await fetch(`${baseUrl}/v1/registration`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${token}`,
        "content-type": "application/json",
      },
    });
    if (!response.ok) throw new Error(`Registration confirmation failed: ${response.status}`);
    const result = await response.json() as { newly_created?: boolean; sign_in_provider?: string };
    if (result.newly_created) {
      await trackSignUp(registrationMethod(result.sign_in_provider || "unknown"));
    }
  })();
  registrationChecks.set(user.uid, check);
  void check.catch(() => registrationChecks.delete(user.uid));
  return check;
}

function getEmailLinkContinueUrl() {
  const current = new URL(window.location.href);
  const destination = new URL(current.pathname === "/usage" ? "/usage" : "/", current.origin);
  const packageId = current.searchParams.get("package");
  if (current.pathname === "/usage" && packageId && ["mini_15", "starter", "growth", "scale"].includes(packageId)) destination.searchParams.set("package", packageId);
  const source = current.searchParams.get("source");
  if (source && /^\/(en|zh)\/alternative\/trafficguard$/.test(source)) destination.searchParams.set("source", source);
  return destination.toString();
}

export async function getAccessToken() {
  return firebaseAuth?.currentUser ? firebaseAuth.currentUser.getIdToken() : null;
}

export async function getTenantId() {
  const user = firebaseAuth?.currentUser;
  if (!user) return null;
  const token = await user.getIdTokenResult();
  return typeof token.claims.tenant_id === "string" && token.claims.tenant_id.trim()
    ? token.claims.tenant_id
    : user.uid;
}

interface AuthContextValue {
  user: Pick<User, "uid" | "email" | "displayName"> | null;
  loading: boolean;
  demo: boolean;
  emailLinkPending: boolean;
  signInWithGoogle: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  completeMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthContextValue["user"]>(
    demoMode ? { uid: "demo_user", email: "dan@acme.example", displayName: "Dan Wenchuan" } : null,
  );
  const [loading, setLoading] = useState(!demoMode);
  const emailLinkPending = Boolean(
    firebaseAuth && isSignInWithEmailLink(firebaseAuth, window.location.href),
  );

  useEffect(() => {
    if (!firebaseAuth) {
      setLoading(false);
      return;
    }
    // Keep the app on its neutral loading screen while an email-link sign-in
    // is being exchanged. Otherwise Firebase's initial unauthenticated event
    // can briefly render the login page before the authenticated event arrives.
    const completingEmailLink = emailLinkPending && Boolean(
      window.localStorage.getItem(emailForSignInStorageKey),
    );
    const unsubscribe = onAuthStateChanged(firebaseAuth, (nextUser) => {
      if (!nextUser) {
        setUser(null);
        if (!completingEmailLink) setLoading(false);
        return;
      }
      setLoading(true);
      void confirmRegistration(nextUser)
        .then(() => setUser(nextUser))
        .catch((error: unknown) => {
          console.error("Unable to confirm registration.", error);
          setUser(null);
        })
        .finally(() => {
          if (!completingEmailLink) setLoading(false);
        });
    });
    const savedEmail = window.localStorage.getItem(emailForSignInStorageKey);
    if (emailLinkPending && savedEmail) {
      void signInWithEmailLink(firebaseAuth, savedEmail, window.location.href)
        .then(async (result) => {
          await confirmRegistration(result.user);
          setUser(result.user);
          setLoading(false);
        })
        .catch((error: unknown) => {
          console.error("Unable to complete email-link sign-in.", error);
          setLoading(false);
        })
        .finally(() => {
          window.localStorage.removeItem(emailForSignInStorageKey);
        });
    }
    return unsubscribe;
  }, [emailLinkPending]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      demo: demoMode,
      emailLinkPending,
      signInWithGoogle: async () => {
        if (!firebaseAuth) throw new Error("Firebase Authentication is not configured.");
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        // The popup closes before the auth observer necessarily publishes the
        // new user. Hold the application at its neutral loading screen so the
        // user never sees the login page flash between those two events.
        setLoading(true);
        try {
          const result = await signInWithPopup(firebaseAuth, provider);
          await confirmRegistration(result.user);
          setUser(result.user);
          setLoading(false);
        } catch (error) {
          setLoading(false);
          throw error;
        }
      },
      sendMagicLink: async (email) => {
        if (!firebaseAuth) throw new Error("Firebase Authentication is not configured.");
        await sendSignInLinkToEmail(firebaseAuth, email, {
          url: getEmailLinkContinueUrl(),
          handleCodeInApp: true,
        });
        window.localStorage.setItem(emailForSignInStorageKey, email);
      },
      completeMagicLink: async (email) => {
        if (!firebaseAuth) throw new Error("Firebase Authentication is not configured.");
        setLoading(true);
        try {
          const result = await signInWithEmailLink(firebaseAuth, email, window.location.href);
          await confirmRegistration(result.user);
          setUser(result.user);
          setLoading(false);
          window.localStorage.removeItem(emailForSignInStorageKey);
        } catch (error) {
          setLoading(false);
          throw error;
        }
      },
      signOut: async () => {
        if (!firebaseAuth) return;
        await firebaseSignOut(firebaseAuth);
      },
      updateDisplayName: async (displayName) => {
        const currentUser = firebaseAuth?.currentUser;
        if (!currentUser) {
          if (demoMode) setUser((current) => current ? { ...current, displayName } : current);
          return;
        }
        await updateProfile(currentUser, { displayName });
        await currentUser.getIdToken(true);
        setUser({ uid: currentUser.uid, email: currentUser.email, displayName: currentUser.displayName });
      },
    }),
    [emailLinkPending, loading, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
